import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt';
import {
  AlertListModule,
  ApiModule,
  EmployerService,
  ErrorLogService,
  I18nHelperModule,
  LOGGING_ERROR_HANDLER_PROVIDERS,
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  QueryStringService,
  TranslateHttpLoader,
  TranslateLoader,
  TranslateModule,
  TranslationSelectorModule,
  TranslationSelectorService,
  IsMobileService,
} from '@xref/ngx';
import { Angulartics2Module } from 'angulartics2';
import * as Cookies from 'js-cookie';
import { Router } from "@angular/router";

import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { AuthService } from './services/auth/auth.service';
import { BackdropService } from './services/backdrop/backdrop.service';
import { BuilderService } from './services/builder/builder.service';
import { EditDialogService } from './services/edit-dialog/edit-dialog.service';
import { EditModeService } from './services/edit-mode/edit-mode.service';
import { GetStartedService } from './services/get-started/get-started.service';
import { IndustriesService } from './services/industries/industries.service';
import { AuthInterceptor } from './services/interceptor/http-interceptor.service';
import { MarketingLeadService } from './services/marketing-lead/marketing-lead.service';
import { QuestionnaireResolverService } from './services/questionnaire-resolver/questionnaire-resolver.service';
import { RouteChangeService } from './services/route-change/route-change.service';
import { SaveDialogService } from './services/save-dialog/save-dialog.service';
import { ScreenSizeService } from './services/screen-size/screen-size.service';
import { SearchMenuService } from './services/search-menu/search-menu.service';
import { SearchService } from './services/search/search.service';
import { SettingsMenuService } from './services/settings-menu/settings-menu.service';
import { SuggestionService } from './services/suggestion/suggestion.service';
import { TokenManagementService } from './services/token-management/token-management.service';
import { VerifyCodeDialogService } from './services/verify-code-dialog/verify-code-dialog.service';
import Constants from './shared/constants';
import { AppRoutingModule } from './shared/router';
import { SharedModule } from './shared/shared.module';
import { UserSaveSendDialogService } from './services/user-save-send-dialog/user-save-send-dialog.service';
import { NgxPageScrollModule } from 'ngx-page-scroll';


const apiConfig = {
  url: environment.API
};

const i18nHelperConfig = {
  LOCALE: Constants.LOCALE,
  LOCALE_TOKEN: Constants.LOCALE_TOKEN,
  SUPPORTED_LOCALES: Constants.SUPPORTED_LOCALES,
  storageType: 'cookie',
  localeStorageKey: Constants.LOCALE_TOKEN,
  DOMAIN: environment.DOMAIN
};

export function jwtOptionsFactory() {
  return {
    tokenName: Constants.QUESTIONNAIRE_TOKEN,
    headerName: 'Authorization',
    authScheme: 'JWT ',
    tokenGetter: (() => Cookies.get(Constants.QUESTIONNAIRE_TOKEN)),
    globalHeaders: [
      { 'Content-Type': 'application/json' },
      { 'Accept': 'application/json' }
    ],
    noClientCheck: true,
    allowedDomains: Constants.AUTH_DOMAIN_WHITELIST
  };
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '/i18n/', '.json');
}

export class MissingI18nHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    console.error(`Missing translation ID: ${params.key}`);
    return params.key.substr(params.key.lastIndexOf('.') + 1);
  }
}

@NgModule({
  declarations: [
    AppComponent
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    SharedModule,
    AlertListModule.forRoot(),
    TranslateModule.forRoot({
      missingTranslationHandler: { provide: MissingTranslationHandler, useClass: MissingI18nHandler },
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    I18nHelperModule.forRoot(i18nHelperConfig),
    ApiModule.forRoot(apiConfig),
    Angulartics2Module.forRoot(),
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: jwtOptionsFactory
      }
    }),
    TranslationSelectorModule
  ],
  providers: [
    AuthService,
    EmployerService,
    TranslationSelectorService,
    LOGGING_ERROR_HANDLER_PROVIDERS,
    ErrorLogService,
    SettingsMenuService,
    SuggestionService,
    SearchService,
    RouteChangeService,
    BuilderService,
    EditModeService,
    ScreenSizeService,
    BackdropService,
    GetStartedService,
    SearchMenuService,
    TokenManagementService,
    QueryStringService,
    IndustriesService,
    QuestionnaireResolverService,
    EditDialogService,
    SaveDialogService,
    VerifyCodeDialogService,
    MarketingLeadService,
    UserSaveSendDialogService,
    IsMobileService,
    Title,
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    provideHttpClient(withInterceptorsFromDi()),
  ]
})
export class AppModule { }
